import {
  Component,
  OnInit,
  Input,
  HostBinding,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';

import { ArrayHelper } from '../../utils/array-helper';
import { AppConfigService } from '../../services/app-config.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-multi-dropdown',
  templateUrl: './multi-dropdown.component.html',
  styleUrls: ['./multi-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiDropdownComponent implements OnInit {

  private innerValue: string;

  internalItems: IMultiDropdownData[];

  debugMode: boolean;

  @HostBinding('class.input-group') inputGroup = true;

  @HostBinding('class.multi-dropdown') multiDropdown = true;

  @ViewChild('dropDown') dropDown: NgbDropdown;

  @Input() ariaDescribedby: string;

  @Input() disabled: boolean;

  @Output() valueChange = new EventEmitter<string>();

  /**
   * selectData-property
   *
   * @memberof MultiDropdownComponent
   */
  @Input() set selectData(value: IMultiDropdownData[]) {
    const current = this.getValues().map(x => x.toUpperCase());
    this.internalItems = value.map((x) => ({ 
      key: x.key,
      text: x.text,
      selected: ArrayHelper.contains(current, x.key.toUpperCase())
    }));
    this.propagateValue();
  }

  /**
   * The value - the only source of truth
   *
   * @type {string}
   * @memberof MultiDropdownComponent
   */
  @Input() set value(value: string) {
    if (this.innerValue === value) { return; }

    const availableKeys = this.internalItems.map(kv => kv.key);
    const choosableOptions = (value || '').split(FORM_VALUE_SEPARATOR)
    .filter(vt => availableKeys.includes(vt))
    .join(FORM_VALUE_SEPARATOR);

    if (this.innerValue === choosableOptions) { return; }
    
    this.innerValue = choosableOptions;
    this.setCheckedState();
  }
  get value(): string {
    return this.innerValue;
  }

  constructor(private cd: ChangeDetectorRef) { }

  ngOnInit() {
    this.debugMode = AppConfigService.settings.debug;
  }

  private getValues(): string[] {
    return (this.innerValue || '').split(FORM_VALUE_SEPARATOR);
  }

  /**
   * Set checkboxes according to current form value
   *
   * @private
   * @memberof MultiDropdownComponent
   */
  private setCheckedState() {
    const values = this.getValues().map(x => x.toUpperCase());
    this.internalItems.forEach(ii => ii.selected = values.includes(ii.key.toUpperCase()));
  }

  /**
   * Propagate selected items to form (only if changed)
   *
   * @private
   * @memberof MultiDropdownComponent
   */
  private propagateValue() {
    if (this.internalItems === undefined) {
      return;
    }

    const newValue = this.internalItems.filter((x) => x.selected).map((x) => x.key).join(FORM_VALUE_SEPARATOR);
    if (this.innerValue !== undefined) {
      this.setInnerValue(newValue);
    }
  }

  /**
   * returns a base64 "hash" of the given item
   * used as html element id
   *
   * @param {IMultiDropdownData} item
   * @returns
   * @memberof MultiDropdownComponent
   */
  getItemId(item: IMultiDropdownData): string {
    return item.key + '-' + btoa(item.text);
  }

  /**
   * handle the unselect event on the remove button
   *
   * @param {string} key
   * @memberof MultiDropdownComponent
   */
  onToggle(key: string, event: MouseEvent): void {
    if (this.disabled) {
      event.stopImmediatePropagation();
      return;
    }

    const item = this.internalItems.find(x => x.key === key);
    if (item) {
      item.selected = !item.selected;
    }

    this.propagateValue();
  }

  /**
   * Handle click on label or bullet
   * spec: don't close dropdown when selecting via bullet (always close when unselecting or clicking on label-text)
   *
   * @param {boolean} isSelected
   * @param {MouseEvent} event
   * @memberof MultiDropdownComponent
   */
  onClickLabel(isSelected: boolean, event: MouseEvent): void {
    // not clicked on bullet or was selected
    if (event.offsetX >= 0 || isSelected) {
      this.dropDown.close();
    }
  }

  /**
   * mimics the behavior of clicking on the dropdown
   * label when clicked on row, but outside the label
   * @param itemKey dropdown item key
   * @param event click event object
   */
  mimicClickOnLabel(itemKey: string, event: MouseEvent): void {
    // process only own click triggeres, ignore all others
    if (event.target !== event.currentTarget) { return; }

    event.stopPropagation();
    event.preventDefault();

    const rowData = this.internalItems.find(item => item.key === itemKey);

    if (rowData) {
      rowData.selected = !rowData.selected;
    }
    this.propagateValue();
    this.onClickLabel(rowData.selected, event);
  }

  private setInnerValue(newValue: string) {
    if (this.innerValue !== newValue) {
      this.cd.markForCheck();
      this.innerValue = newValue;
      this.valueChange.emit(newValue);
    }

  }

}

// Value separator for MultiDropdown-Component values
export const FORM_VALUE_SEPARATOR = ',';

/**
 * data item for MultiDropDown component
 *
 * @export
 * @class IMultiDropdownData
 */
export class IMultiDropdownData {

  /**
   * key of the element
   *
   * @type {(string)}
   * @memberof IMultiDropdownData
   */
  key: string;

  /**
   * text of the element
   *
   * @type {string}
   * @memberof IMultiDropdownData
   */
  text: string;

  /**
   * true if the item is selected
   *
   * @type {boolean}
   * @memberof IMultiDropdownData
   */
  selected: boolean;
}
